/** IPv4 literals must not be treated as multi-label FQDNs (e.g. `127.0.0.1` has 4 segments). */
function isIpLiteralHost(hostname: string): boolean {
  if (hostname.includes(':')) {
    return true
  }
  const parts = hostname.split('.')
  return (
    parts.length === 4 &&
    parts.every((p) => /^\d{1,3}$/.test(p))
  )
}

/** Dev/QA/Staging roots are `{env}.bridgebss.com` (3 labels) — do not strip the env name. */
function isBridgeBssEnvRoot(parts: string[]): boolean {
  return (
    parts.length >= 3 &&
    parts.at(-2) === 'bridgebss' &&
    parts.at(-1) === 'com'
  )
}

/**
 * Environment registrable root (marketing apex), without product or tenant label.
 * - `acme.localhost` / `app.localhost` → `localhost`
 * - `app.brushfire.com` / `acme.brushfire.com` → `brushfire.com`
 * - `app.brushfire-qa.bridgebss.com` → `brushfire-qa.bridgebss.com`
 * - `brushfire-qa.bridgebss.com` → unchanged (3-label Bridge BSS env root)
 */
export function getEnvironmentRootHostname(hostname: string): string {
  if (hostname === 'localhost' || hostname.endsWith('.localhost')) {
    return 'localhost'
  }
  if (isIpLiteralHost(hostname)) {
    return hostname
  }

  const parts = hostname.split('.')

  if (isBridgeBssEnvRoot(parts)) {
    if (parts.length === 3) {
      return hostname
    }
    return parts.slice(1).join('.')
  }

  if (parts.length >= 4) {
    return parts.slice(1).join('.')
  }

  if (parts.length === 3) {
    // Production-style `{label}.example.com` — strip product or tenant label.
    return parts.slice(1).join('.')
  }

  return hostname
}

/**
 * Sensemaker public auth host (/login, forgot-password, …).
 * Always `app.{environmentRoot}` — marketing owns the bare root.
 * - `acme.localhost` → `app.localhost`
 * - `acme.brushfire.com` → `app.brushfire.com`
 * - `acme.brushfire-qa.bridgebss.com` → `app.brushfire-qa.bridgebss.com`
 */
export function getPublicAuthApexHostname(hostname: string): string {
  if (isIpLiteralHost(hostname)) {
    return hostname
  }
  const root = getEnvironmentRootHostname(hostname)
  if (root === 'localhost') {
    return 'app.localhost'
  }
  return `app.${root}`
}

/**
 * Compute the hostname the browser should use for a tenant user.
 * Tenant is always the first DNS label on the environment root — never nested
 * under `app.` (e.g. `acme.brushfire.com`, not `acme.app.brushfire.com`).
 */
export function buildTenantHostname(hostname: string, tenantSub: string): string {
  const ts = tenantSub.trim()
  if (!ts) {
    return hostname
  }
  if (isIpLiteralHost(hostname)) {
    return hostname
  }

  const root = getEnvironmentRootHostname(hostname)
  const expected =
    root === 'localhost' ? `${ts}.localhost` : `${ts}.${root}`
  if (hostname === expected) {
    return hostname
  }
  return expected
}
